import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  IcoSource,
  PendingStoryousIntegrationRequestsQuery,
  StoryousMatchSuggestion,
  StoryousMerchantPlace,
  VenueForStoryousConnection,
  useConnectStoryousIntegrationMutation,
  useMarkStoryousIntegrationRequestCompletedMutation,
  usePendingStoryousIntegrationRequestsQuery,
  useRejectStoryousIntegrationRequestMutation,
  useStoryousMatchSuggestionsLazyQuery,
  useVenuesForStoryousConnectionQuery,
} from '../../generated/graphql';

type VenueOption = VenueForStoryousConnection;
type PendingRequest =
  PendingStoryousIntegrationRequestsQuery['pendingStoryousIntegrationRequests'][number];

interface SelectedPlace {
  merchantId: string;
  merchantName: string;
  placeId: string;
  placeName: string;
  businessId?: string | null;
}

interface SlackTicketData {
  firstName?: string;
  lastName?: string;
  venueName?: string;
  startDate?: string;
  email?: string;
  accountId?: string;
  venueId?: string;
}

const REQUIRED_SLACK_FIELDS = [
  'firstName',
  'lastName',
  'venueName',
  'startDate',
  'email',
  'accountId',
  'venueId',
] as const;

const normalizeIco = (ico: string | null | undefined): string => {
  if (!ico) return '';
  return ico.trim().toLowerCase().replace(/\s+/g, '');
};

const validateSlackJson = (
  jsonString: string,
): { valid: boolean; data: SlackTicketData | null; errors: string[] } => {
  if (!jsonString.trim()) {
    return { valid: false, data: null, errors: [] };
  }

  const sanitized = jsonString
    .replace(/\u201C|\u201D|\u201E|\u201F|\u2033|\u2036|\u3003|\uFF02/g, '"')
    .replace(/\u2018|\u2019|\u201A|\u201B|\u2032|\u2035/g, "'")
    .replace(/\u200B|\u200C|\u200D|\uFEFF/g, '')
    .replace(/\u00A0/g, ' ');

  let parsed: unknown;
  try {
    parsed = JSON.parse(sanitized);
  } catch (error) {
    return {
      valid: false,
      data: null,
      errors: [
        'Invalid JSON format. Please check for syntax errors or try copying again.',
      ],
    };
  }

  if (typeof parsed !== 'object' || parsed === null) {
    return { valid: false, data: null, errors: ['JSON must be an object'] };
  }

  const parsedObj = parsed as Record<string, unknown>;
  const missingFields = REQUIRED_SLACK_FIELDS.filter(
    (field) => !(field in parsedObj),
  );

  if (missingFields.length > 0) {
    return {
      valid: false,
      data: parsed as SlackTicketData,
      errors: missingFields.map((f) => `Missing required field: ${f}`),
    };
  }

  return { valid: true, data: parsed as SlackTicketData, errors: [] };
};

interface RejectDialogProps {
  open: boolean;
  onClose: () => void;
  onReject: (notes: string | undefined) => Promise<void>;
}

const RejectDialog = ({ open, onClose, onReject }: RejectDialogProps) => {
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    await onReject(notes || undefined);
    setIsSubmitting(false);
    setNotes('');
  };

  const handleClose = () => {
    setNotes('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reject Integration Request</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Optionally provide a reason for rejecting this request.
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g., Customer not found in Storyous, invalid ICO..."
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="error"
          disabled={isSubmitting}
        >
          Reject Request
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const StoryousIntegrationPage = () => {
  const [selectedVenueId, setSelectedVenueId] = useState('');
  const [selectedPlaces, setSelectedPlaces] = useState<SelectedPlace[]>([]);
  const [startDate, setStartDate] = useState<DateTime | null>(DateTime.now());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [skipIcoValidation, setSkipIcoValidation] = useState(false);
  const [expandedMerchants, setExpandedMerchants] = useState<Set<string>>(
    new Set(),
  );
  const [expandedPlaceDetails, setExpandedPlaceDetails] = useState<Set<string>>(
    new Set(),
  );
  const [slackJson, setSlackJson] = useState('');
  const [slackJsonValidation, setSlackJsonValidation] = useState<{
    valid: boolean;
    data: SlackTicketData | null;
    errors: string[];
  }>({ valid: false, data: null, errors: [] });
  const [isConnecting, setIsConnecting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: venuesData, loading: venuesLoading } =
    useVenuesForStoryousConnectionQuery();

  const { data: pendingRequestsData, refetch: refetchPendingRequests } =
    usePendingStoryousIntegrationRequestsQuery();

  const [
    loadSuggestions,
    { data: suggestionsData, loading: suggestionsLoading },
  ] = useStoryousMatchSuggestionsLazyQuery();

  const [connectIntegration] = useConnectStoryousIntegrationMutation();

  const [markRequestCompleted] =
    useMarkStoryousIntegrationRequestCompletedMutation();

  const [rejectRequest] = useRejectStoryousIntegrationRequestMutation();

  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingRequestId, setRejectingRequestId] = useState<string | null>(
    null,
  );

  const venues = useMemo<VenueOption[]>(() => {
    if (!venuesData?.venuesForStoryousConnection) return [];
    return [...venuesData.venuesForStoryousConnection].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [venuesData]);

  const selectedVenue = venues.find((v) => v.id === selectedVenueId) ?? null;

  const suggestions = useMemo<StoryousMatchSuggestion[]>(() => {
    if (!suggestionsData?.storyousMatchSuggestions) return [];
    return suggestionsData.storyousMatchSuggestions;
  }, [suggestionsData]);

  const pendingRequests = useMemo<PendingRequest[]>(() => {
    if (!pendingRequestsData?.pendingStoryousIntegrationRequests) return [];
    return pendingRequestsData.pendingStoryousIntegrationRequests;
  }, [pendingRequestsData]);

  const groupedSuggestions = useMemo(() => {
    const groups = new Map<
      string,
      {
        merchant: { id: string; name: string; businessId?: string | null };
        places: StoryousMatchSuggestion[];
      }
    >();

    suggestions.forEach((s) => {
      const key = s.merchantPlace.merchantId;
      const existing = groups.get(key);
      if (existing) {
        existing.places.push(s);
      } else {
        groups.set(key, {
          merchant: {
            id: s.merchantPlace.merchantId,
            name: s.merchantPlace.merchantName,
            businessId: s.merchantPlace.businessId,
          },
          places: [s],
        });
      }
    });

    return Array.from(groups.values()).sort((a, b) => {
      const aMaxScore = Math.max(...a.places.map((p) => p.matchScore));
      const bMaxScore = Math.max(...b.places.map((p) => p.matchScore));
      const aHasIco = a.places.some((p) => p.icoMatch);
      const bHasIco = b.places.some((p) => p.icoMatch);
      if (aHasIco !== bHasIco) return aHasIco ? -1 : 1;
      return bMaxScore - aMaxScore;
    });
  }, [suggestions]);

  const handleSelectPendingRequest = (request: PendingRequest) => {
    setSelectedVenueId(request.venueId);
    setSelectedPlaces([]);
    setSearchTerm('');
    setSlackJson('');
    setSlackJsonValidation({ valid: false, data: null, errors: [] });
    setExpandedMerchants(new Set());
    setExpandedPlaceDetails(new Set());
    if (request.startDate) {
      setStartDate(DateTime.fromMillis(request.startDate));
    }
    loadSuggestions({
      variables: {
        venueId: request.venueId,
        venueName: request.venue.name,
      },
    });
  };

  const handleMarkRequestCompleted = async (requestId: string) => {
    await markRequestCompleted({
      variables: { id: requestId },
      toastConfig: {
        successText: 'Request marked as completed',
        errorText: 'Failed to mark request as completed',
      },
    });
    refetchPendingRequests();
  };

  const openRejectDialog = (requestId: string) => {
    setRejectingRequestId(requestId);
    setRejectDialogOpen(true);
  };

  const handleRejectRequest = async (notes: string | undefined) => {
    if (!rejectingRequestId) return;
    await rejectRequest({
      variables: {
        id: rejectingRequestId,
        notes,
      },
      toastConfig: {
        successText: 'Request rejected',
        errorText: 'Failed to reject request',
      },
    });
    setRejectDialogOpen(false);
    setRejectingRequestId(null);
    refetchPendingRequests();
  };

  const handleVenueChange = (
    _: React.SyntheticEvent<Element, Event>,
    value: VenueOption | null,
  ) => {
    setSelectedVenueId(value?.id ?? '');
    setSelectedPlaces([]);
    setSearchTerm('');
    setSlackJson('');
    setSlackJsonValidation({ valid: false, data: null, errors: [] });
    setExpandedMerchants(new Set());
    setExpandedPlaceDetails(new Set());
    if (value) {
      loadSuggestions({
        variables: {
          venueId: value.id,
          venueName: value.name,
        },
      });
    }
  };

  const handleSlackJsonChange = (value: string) => {
    setSlackJson(value);
    const validation = validateSlackJson(value);
    setSlackJsonValidation(validation);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (validation.valid && validation.data) {
      const { data } = validation;
      if (data.venueId) {
        setSelectedVenueId(data.venueId);
      }
      if (data.startDate) {
        const parsed = DateTime.fromISO(data.startDate);
        if (parsed.isValid) {
          setStartDate(parsed);
        }
      }
      setSelectedPlaces([]);
      setSearchTerm('');
      setExpandedMerchants(new Set());
      setExpandedPlaceDetails(new Set());

      debounceTimerRef.current = setTimeout(() => {
        if (data.venueId) {
          loadSuggestions({
            variables: {
              venueId: data.venueId,
              firstName: data.firstName,
              lastName: data.lastName,
              venueName: data.venueName,
            },
          });
        }
      }, 500);
    }
  };

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    setSelectedPlaces([]);
    setExpandedMerchants(new Set());
    setExpandedPlaceDetails(new Set());
    loadSuggestions({
      variables: {
        venueId: selectedVenueId,
        venueName: selectedVenue?.name,
        searchTerm: searchTerm.trim() || undefined,
      },
    });
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const toggleMerchant = (merchantId: string) => {
    setExpandedMerchants((prev) => {
      const next = new Set(prev);
      if (next.has(merchantId)) {
        next.delete(merchantId);
      } else {
        next.add(merchantId);
      }
      return next;
    });
  };

  const togglePlaceDetails = (placeKey: string) => {
    setExpandedPlaceDetails((prev) => {
      const next = new Set(prev);
      if (next.has(placeKey)) {
        next.delete(placeKey);
      } else {
        next.add(placeKey);
      }
      return next;
    });
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const togglePlaceSelection = (place: StoryousMerchantPlace) => {
    setSelectedPlaces((prev) => {
      const exists = prev.some(
        (p) => p.merchantId === place.merchantId && p.placeId === place.placeId,
      );
      if (exists) {
        return prev.filter(
          (p) =>
            !(p.merchantId === place.merchantId && p.placeId === place.placeId),
        );
      }
      return [
        ...prev,
        {
          merchantId: place.merchantId,
          merchantName: place.merchantName,
          placeId: place.placeId,
          placeName: place.placeName,
          businessId: place.businessId,
        },
      ];
    });
  };

  const isPlaceSelected = (place: StoryousMerchantPlace) =>
    selectedPlaces.some(
      (p) => p.merchantId === place.merchantId && p.placeId === place.placeId,
    );

  const hasIcoMismatch = useMemo(() => {
    if (!selectedVenue?.ico) return false;
    const venueIco = normalizeIco(selectedVenue.ico);
    return selectedPlaces.some(
      (p) => p.businessId && normalizeIco(p.businessId) !== venueIco,
    );
  }, [selectedVenue, selectedPlaces]);

  const openConfirmDialog = () => {
    if (!selectedVenueId || selectedPlaces.length === 0 || !startDate) return;
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (!startDate) return;

    setIsConnecting(true);
    try {
      await Promise.all(
        selectedPlaces.map((place) =>
          connectIntegration({
            variables: {
              venueId: selectedVenueId,
              merchantId: place.merchantId,
              placeId: place.placeId,
              startDate: startDate.toISO() ?? '',
              skipIcoValidation: hasIcoMismatch ? skipIcoValidation : undefined,
            },
            toastConfig: {
              successText: `Connected ${place.placeName} successfully`,
              errorText: `Failed to connect ${place.placeName}`,
            },
          }),
        ),
      );

      setConfirmOpen(false);
      setSelectedVenueId('');
      setSelectedPlaces([]);
      setStartDate(DateTime.now());
      setSkipIcoValidation(false);
    } finally {
      setIsConnecting(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'success';
    if (score >= 70) return 'info';
    if (score >= 50) return 'warning';
    return 'default';
  };

  return (
    <>
      <Box
        width="100%"
        p={3}
        pb={selectedPlaces.length > 0 ? 15 : 3}
        display="flex"
        justifyContent="center"
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
            gap: 2,
            maxWidth: '1400px',
            width: '100%',
          }}
        >
          <Card sx={{ p: 3, backgroundColor: '#FAFBFF' }}>
            <Typography variant="h5" gutterBottom>
              Storyous Integration
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{ mt: 2, mb: 1, fontWeight: 600, color: 'primary.main' }}
            >
              Step 1: Select Venue
            </Typography>

            <Autocomplete<VenueOption>
              fullWidth
              value={selectedVenue}
              options={venues}
              getOptionLabel={(o) => o.name}
              loading={venuesLoading}
              onChange={handleVenueChange}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography>{option.name}</Typography>
                    {option.ico && (
                      <Typography variant="caption" color="text.secondary">
                        ICO: {option.ico}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Venue"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: venuesLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      params.InputProps.endAdornment
                    ),
                  }}
                />
              )}
            />

            {selectedVenue && (
              <Box mt={2} p={2} bgcolor="#F3F6FF" borderRadius={1}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="body2">
                    <strong>Venue ID:</strong>
                  </Typography>
                  <Typography variant="body2" fontFamily="monospace">
                    {selectedVenue.id}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => copyToClipboard(selectedVenue.id)}
                    sx={{ p: 0.25 }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="body2">
                    <strong>ICO:</strong> {selectedVenue.ico || 'Not set'}
                  </Typography>
                  {selectedVenue.icoSource && (
                    <Chip
                      size="small"
                      label={
                        selectedVenue.icoSource === IcoSource.LegalEntity
                          ? 'from Legal Entity'
                          : 'from Billing Details'
                      }
                      variant="outlined"
                      sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                  {selectedVenue.ico && (
                    <IconButton
                      size="small"
                      onClick={() =>
                        copyToClipboard(selectedVenue.ico as string)
                      }
                      sx={{ p: 0.25 }}
                    >
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                </Box>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="body2">
                    <strong>Legal Name:</strong>{' '}
                    {selectedVenue.legalName || 'Not set'}
                  </Typography>
                  {selectedVenue.legalName && (
                    <IconButton
                      size="small"
                      onClick={() =>
                        copyToClipboard(selectedVenue.legalName as string)
                      }
                      sx={{ p: 0.25 }}
                    >
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                </Box>
                {selectedVenue.existingStoryousConnections.length > 0 && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      <strong>
                        Already connected to{' '}
                        {selectedVenue.existingStoryousConnections.length}{' '}
                        Storyous place(s)
                      </strong>
                    </Typography>
                    {selectedVenue.existingStoryousConnections.map((conn) => (
                      <Box
                        key={`${conn.merchantId}-${conn.placeId}`}
                        sx={{ mt: 1, pl: 1, borderLeft: '2px solid #90caf9' }}
                      >
                        <Typography variant="body2" fontWeight="medium">
                          {conn.placeName}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography variant="caption" color="text.secondary">
                            Merchant ID:
                          </Typography>
                          <Typography
                            variant="caption"
                            fontFamily="monospace"
                            bgcolor="grey.100"
                            px={0.5}
                            borderRadius={0.5}
                          >
                            {conn.merchantId}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => copyToClipboard(conn.merchantId)}
                            sx={{ p: 0.25 }}
                            title="Copy Merchant ID"
                          >
                            <ContentCopyIcon sx={{ fontSize: 12 }} />
                          </IconButton>
                        </Box>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography variant="caption" color="text.secondary">
                            Place ID:
                          </Typography>
                          <Typography
                            variant="caption"
                            fontFamily="monospace"
                            bgcolor="grey.100"
                            px={0.5}
                            borderRadius={0.5}
                          >
                            {conn.placeId}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => copyToClipboard(conn.placeId)}
                            sx={{ p: 0.25 }}
                            title="Copy Place ID"
                          >
                            <ContentCopyIcon sx={{ fontSize: 12 }} />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Alert>
                )}
              </Box>
            )}

            {selectedVenueId && (
              <>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mt: 3,
                    mb: 1,
                    fontWeight: 600,
                    color: 'primary.main',
                  }}
                >
                  Step 2: Select Storyous Instance(s)
                </Typography>

                <Box
                  component="form"
                  onSubmit={handleSearch}
                  display="flex"
                  gap={1}
                  mb={2}
                >
                  <TextField
                    size="small"
                    fullWidth
                    label="Search Storyous by ICO, merchant or place name"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Button
                    type="submit"
                    variant="outlined"
                    disabled={suggestionsLoading}
                  >
                    Search
                  </Button>
                </Box>

                {suggestionsLoading && (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    p={3}
                  >
                    <CircularProgress />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 2 }}
                    >
                      Loading Storyous merchants... This may take a moment.
                    </Typography>
                  </Box>
                )}

                {!suggestionsLoading && groupedSuggestions.length > 0 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Match scores</strong> compare venue name &quot;
                      {selectedVenue?.name}&quot;
                      {selectedVenue?.legalName &&
                        ` and legal name "${selectedVenue.legalName}"`}
                      {selectedVenue?.ico && ` (ICO: ${selectedVenue.ico})`}{' '}
                      against Storyous merchant/place names.
                      <br />
                      <strong>100%</strong> = ICO match | <strong>95%</strong> =
                      exact name | <strong>85%</strong> = partial match | Lower
                      = fuzzy match
                    </Typography>
                  </Alert>
                )}

                {!suggestionsLoading && groupedSuggestions.length === 0 && (
                  <Alert severity="warning">
                    No Storyous instances found. The customer may not have the
                    Dishboard feature enabled in Storyous.
                  </Alert>
                )}

                {!suggestionsLoading && groupedSuggestions.length > 0 && (
                  <Box
                    sx={{
                      maxHeight: '400px',
                      overflow: 'auto',
                    }}
                  >
                    {groupedSuggestions.map((group) => {
                      const isExpanded = expandedMerchants.has(
                        group.merchant.id,
                      );
                      const maxScore = Math.max(
                        ...group.places.map((p) => p.matchScore),
                      );
                      const hasIco = group.places.some((p) => p.icoMatch);
                      const activePlaces = group.places.filter(
                        (p) => p.merchantPlace.placeState === 'active',
                      );
                      const inactivePlaces = group.places.filter(
                        (p) => p.merchantPlace.placeState !== 'active',
                      );
                      const connectedPlaces = group.places.filter(
                        (p) => p.merchantPlace.isAlreadyConnected,
                      );

                      return (
                        <Card
                          key={group.merchant.id}
                          sx={{
                            mb: 1,
                            border: hasIco ? '2px solid #4caf50' : undefined,
                          }}
                        >
                          <Box
                            sx={{
                              p: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                            }}
                            onClick={() => toggleMerchant(group.merchant.id)}
                          >
                            <IconButton size="small">
                              {isExpanded ? (
                                <ExpandLessIcon />
                              ) : (
                                <ExpandMoreIcon />
                              )}
                            </IconButton>
                            <Box flex={1} ml={1}>
                              <Typography variant="subtitle1">
                                {group.merchant.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                component="div"
                              >
                                {activePlaces.length} active
                                {inactivePlaces.length > 0 &&
                                  `, ${inactivePlaces.length} inactive`}
                                {connectedPlaces.length > 0 &&
                                  `, ${connectedPlaces.length} already connected`}{' '}
                                | ICO: {group.merchant.businessId || 'N/A'}
                              </Typography>
                            </Box>
                            <Box display="flex" gap={0.5}>
                              {hasIco && (
                                <Chip
                                  size="small"
                                  icon={<CheckCircleIcon />}
                                  label="ICO Match"
                                  color="success"
                                />
                              )}
                              <Chip
                                size="small"
                                label={`${maxScore}%`}
                                color={getScoreColor(maxScore)}
                              />
                            </Box>
                          </Box>

                          <Collapse in={isExpanded}>
                            <List dense sx={{ pl: 4 }}>
                              {group.places.map((suggestion) => {
                                const place = suggestion.merchantPlace;
                                const isSelected = isPlaceSelected(place);
                                const placeKey = `${place.merchantId}:${place.placeId}`;
                                const isDetailsExpanded =
                                  expandedPlaceDetails.has(placeKey);

                                return (
                                  <Box key={place.placeId} sx={{ mb: 0.5 }}>
                                    <ListItem
                                      sx={{
                                        bgcolor: isSelected
                                          ? '#e3f2fd'
                                          : undefined,
                                        borderRadius: 1,
                                      }}
                                    >
                                      <Checkbox
                                        checked={isSelected}
                                        onChange={() =>
                                          togglePlaceSelection(place)
                                        }
                                        disabled={place.isAlreadyConnected}
                                      />
                                      <ListItemText
                                        primary={
                                          <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                          >
                                            <Typography>
                                              {place.placeName}
                                            </Typography>
                                            {place.placeState !== 'active' && (
                                              <Chip
                                                size="small"
                                                label={place.placeState}
                                                color="warning"
                                              />
                                            )}
                                            {place.isAlreadyConnected && (
                                              <Chip
                                                size="small"
                                                label={`Connected to ${place.connectedVenueName}`}
                                                color="error"
                                              />
                                            )}
                                          </Box>
                                        }
                                        secondary={
                                          <>
                                            {place.city && `${place.city}, `}
                                            {place.country} |{' '}
                                            {suggestion.matchedOn}
                                          </>
                                        }
                                      />
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          togglePlaceDetails(placeKey);
                                        }}
                                        sx={{
                                          mr: 1,
                                          color: isDetailsExpanded
                                            ? 'primary.main'
                                            : 'text.secondary',
                                        }}
                                        title="Show technical details"
                                      >
                                        <InfoOutlinedIcon fontSize="small" />
                                      </IconButton>
                                      <Chip
                                        size="small"
                                        label={`${suggestion.matchScore}%`}
                                        color={getScoreColor(
                                          suggestion.matchScore,
                                        )}
                                      />
                                    </ListItem>
                                    <Collapse in={isDetailsExpanded}>
                                      <Box
                                        sx={{
                                          ml: 7,
                                          mr: 2,
                                          mb: 1,
                                          p: 1.5,
                                          bgcolor: '#f5f5f5',
                                          borderRadius: 1,
                                          fontSize: '0.75rem',
                                        }}
                                      >
                                        <Typography
                                          variant="caption"
                                          fontWeight="bold"
                                          display="block"
                                          sx={{ mb: 1 }}
                                        >
                                          Technical Details
                                        </Typography>
                                        <Box
                                          sx={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 1fr',
                                            gap: 0.5,
                                          }}
                                        >
                                          <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={0.5}
                                          >
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                            >
                                              Merchant ID:
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              fontFamily="monospace"
                                            >
                                              {place.merchantId}
                                            </Typography>
                                            <IconButton
                                              size="small"
                                              onClick={() =>
                                                copyToClipboard(
                                                  place.merchantId,
                                                )
                                              }
                                              sx={{ p: 0.25 }}
                                            >
                                              <ContentCopyIcon
                                                sx={{ fontSize: 14 }}
                                              />
                                            </IconButton>
                                          </Box>
                                          <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={0.5}
                                          >
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                            >
                                              Place ID:
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              fontFamily="monospace"
                                            >
                                              {place.placeId}
                                            </Typography>
                                            <IconButton
                                              size="small"
                                              onClick={() =>
                                                copyToClipboard(place.placeId)
                                              }
                                              sx={{ p: 0.25 }}
                                            >
                                              <ContentCopyIcon
                                                sx={{ fontSize: 14 }}
                                              />
                                            </IconButton>
                                          </Box>
                                          <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={0.5}
                                          >
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                            >
                                              Business ID (ICO):
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              fontFamily="monospace"
                                            >
                                              {place.businessId || 'N/A'}
                                            </Typography>
                                            {place.businessId && (
                                              <IconButton
                                                size="small"
                                                onClick={() =>
                                                  copyToClipboard(
                                                    place.businessId as string,
                                                  )
                                                }
                                                sx={{ p: 0.25 }}
                                              >
                                                <ContentCopyIcon
                                                  sx={{ fontSize: 14 }}
                                                />
                                              </IconButton>
                                            )}
                                          </Box>
                                          {place.isAlreadyConnected &&
                                            place.connectedVenueId && (
                                              <Box
                                                display="flex"
                                                alignItems="center"
                                                gap={0.5}
                                              >
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                >
                                                  Connected Venue ID:
                                                </Typography>
                                                <Typography
                                                  variant="caption"
                                                  fontFamily="monospace"
                                                >
                                                  {place.connectedVenueId}
                                                </Typography>
                                                <IconButton
                                                  size="small"
                                                  onClick={() =>
                                                    copyToClipboard(
                                                      place.connectedVenueId as string,
                                                    )
                                                  }
                                                  sx={{ p: 0.25 }}
                                                >
                                                  <ContentCopyIcon
                                                    sx={{ fontSize: 14 }}
                                                  />
                                                </IconButton>
                                              </Box>
                                            )}
                                        </Box>
                                      </Box>
                                    </Collapse>
                                  </Box>
                                );
                              })}
                            </List>
                          </Collapse>
                        </Card>
                      );
                    })}
                  </Box>
                )}
              </>
            )}
          </Card>

          <Box>
            <Card sx={{ p: 3, backgroundColor: '#F5F5F5', mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Paste Slack JSON
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Paste the JSON object from Slack notification to auto-search
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder={`{
  "firstName": "...",
  "lastName": "...",
  "venueName": "...",
  "startDate": "...",
  "email": "...",
  "accountId": "...",
  "venueId": "..."
}`}
                value={slackJson}
                onChange={(e) => handleSlackJsonChange(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: (() => {
                        if (!slackJson) return undefined;
                        return slackJsonValidation.valid
                          ? '#4caf50'
                          : '#f44336';
                      })(),
                      borderWidth: slackJson ? 2 : 1,
                    },
                  },
                }}
              />
              {slackJson && slackJsonValidation.valid && (
                <Alert severity="success" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    <strong>Slack JSON loaded</strong>
                  </Typography>
                  <Typography variant="body2">
                    Customer: {slackJsonValidation.data?.firstName}{' '}
                    {slackJsonValidation.data?.lastName} (
                    {slackJsonValidation.data?.email})
                  </Typography>
                  <Typography variant="body2">
                    Venue: {slackJsonValidation.data?.venueName} (auto-selected)
                  </Typography>
                  <Typography variant="body2">
                    Start Date: {slackJsonValidation.data?.startDate}{' '}
                    (pre-filled)
                  </Typography>
                </Alert>
              )}
              {slackJson && slackJsonValidation.errors.length > 0 && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  <Typography variant="body2" fontWeight="bold">
                    Invalid Slack JSON
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2, mt: 0.5 }}>
                    {slackJsonValidation.errors.map((err) => (
                      <li key={err}>
                        <Typography variant="body2">{err}</Typography>
                      </li>
                    ))}
                  </Box>
                </Alert>
              )}
            </Card>

            {pendingRequests.length > 0 && (
              <Card sx={{ p: 3, backgroundColor: '#FFF8E1', mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Pending Requests ({pendingRequests.length})
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Click on a request to preselect the venue and start date
                </Typography>
                <List dense sx={{ maxHeight: '300px', overflow: 'auto' }}>
                  {pendingRequests.map((request) => (
                    <ListItem
                      key={request.id}
                      sx={{
                        bgcolor:
                          selectedVenueId === request.venueId
                            ? '#FFF3E0'
                            : 'white',
                        borderRadius: 1,
                        mb: 0.5,
                        border: '1px solid #FFE0B2',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#FFF3E0' },
                      }}
                      onClick={() => handleSelectPendingRequest(request)}
                    >
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="subtitle2">
                              {request.venue.name}
                            </Typography>
                            {request.venue.legalEntity
                              ?.companyIdentificationNumber && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                (ICO:{' '}
                                {
                                  request.venue.legalEntity
                                    .companyIdentificationNumber
                                }
                                )
                              </Typography>
                            )}
                          </Box>
                        }
                        secondary={
                          <>
                            Requested:{' '}
                            {DateTime.fromMillis(
                              request.createdAt,
                            ).toLocaleString(DateTime.DATETIME_MED)}
                            <br />
                            Start date:{' '}
                            {DateTime.fromMillis(
                              request.startDate,
                            ).toLocaleString(DateTime.DATE_MED)}
                            <br />
                            By: {request.account.email}
                          </>
                        }
                      />
                      <Box display="flex" gap={0.5}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            openRejectDialog(request.id);
                          }}
                          sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
                        >
                          Reject
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkRequestCompleted(request.id);
                          }}
                          sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
                        >
                          Mark Done
                        </Button>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </Card>
            )}

            <Card
              sx={{ p: 3, backgroundColor: '#FAFBFF', height: 'fit-content' }}
            >
              <Typography variant="h6" gutterBottom>
                Instructions
              </Typography>
              <Box component="ol" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li>
                  <Typography variant="body2">
                    <strong>Select the Dishboard venue</strong> that needs to be
                    connected to Storyous.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <strong>Review suggested matches</strong> - ICO matches are
                    highlighted. Expand merchants to see individual places.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <strong>Select places to connect</strong> - one venue can
                    have multiple Storyous places (e.g., food truck with
                    multiple stalls).
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <strong>Set the start date</strong> and confirm the
                    connection.
                  </Typography>
                </li>
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>ICO Validation:</strong> The system validates that the
                  venue&apos;s ICO matches the Storyous merchant&apos;s
                  businessId. If there&apos;s a mismatch, you&apos;ll need to
                  confirm before proceeding.
                </Typography>
              </Alert>

              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Note:</strong> This tool only supports connecting
                  integrations. To disconnect, contact the development team.
                </Typography>
              </Alert>
            </Card>
          </Box>
        </Box>
      </Box>

      {selectedPlaces.length > 0 && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 -4px 12px rgba(0,0,0,0.1)',
            p: 2,
            zIndex: 1000,
          }}
        >
          <Box
            sx={{
              maxWidth: '1400px',
              margin: '0 auto',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2">
                Selected ({selectedPlaces.length}):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selectedPlaces.map((p) => (
                  <Chip
                    key={`${p.merchantId}-${p.placeId}`}
                    label={p.placeName}
                    size="small"
                    onDelete={() =>
                      togglePlaceSelection({
                        merchantId: p.merchantId,
                        merchantName: p.merchantName,
                        placeId: p.placeId,
                        placeName: p.placeName,
                        businessId: p.businessId,
                      } as StoryousMerchantPlace)
                    }
                  />
                ))}
              </Box>
            </Box>

            {hasIcoMismatch && (
              <Chip
                icon={<WarningIcon />}
                label="ICO Mismatch"
                color="warning"
                size="small"
              />
            )}

            <Box sx={{ flex: 1 }} />

            <LocalizationProvider dateAdapter={AdapterLuxon}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue: DateTime | null) => setStartDate(newValue)}
                renderInput={(params) => (
                  <TextField {...params} size="small" sx={{ width: 180 }} />
                )}
              />
            </LocalizationProvider>

            <Button
              variant="contained"
              onClick={openConfirmDialog}
              disabled={!startDate}
              size="large"
            >
              Connect {selectedPlaces.length} Place(s)
            </Button>
          </Box>
        </Box>
      )}

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Storyous Connection</DialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom>
            <strong>Venue:</strong> {selectedVenue?.name}
          </Typography>
          <Typography gutterBottom>
            <strong>Start Date:</strong> {startDate?.toISODate()}
          </Typography>
          <Typography gutterBottom>
            <strong>Places to connect:</strong>
          </Typography>
          <List dense>
            {selectedPlaces.map((p) => (
              <ListItem key={`${p.merchantId}-${p.placeId}`}>
                <ListItemText
                  primary={p.placeName}
                  secondary={p.merchantName}
                />
              </ListItem>
            ))}
          </List>

          {hasIcoMismatch ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <WarningIcon />
                <Typography variant="subtitle2">ICO Mismatch</Typography>
              </Box>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Venue ICO: {selectedVenue?.ico}
                <br />
                Storyous ICO:{' '}
                {Array.from(
                  new Set(
                    selectedPlaces.map((p) => p.businessId).filter(Boolean),
                  ),
                ).join(', ')}
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={skipIcoValidation}
                    onChange={(e) => setSkipIcoValidation(e.target.checked)}
                  />
                }
                label="I confirm this connection is correct despite ICO mismatch"
                sx={{ mt: 1 }}
              />
            </Alert>
          ) : (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <CheckCircleIcon />
                <Typography>ICO Match Verified</Typography>
              </Box>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={isConnecting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={(hasIcoMismatch && !skipIcoValidation) || isConnecting}
            startIcon={
              isConnecting ? <CircularProgress size={16} /> : undefined
            }
          >
            {isConnecting ? 'Connecting...' : 'Confirm Connection'}
          </Button>
        </DialogActions>
      </Dialog>

      <RejectDialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onReject={handleRejectRequest}
      />
    </>
  );
};
